/**
 * A pair of inputs, one per language — roadmap 4.5, §12.2.
 *
 * Every translated content field in the product is authored this way: the
 * instructor portal now, the institution and organizer portals in Phase 7. The
 * shape of it matters more than it looks:
 *
 * * **Both languages are visible at once**, not behind a tab. A tab hides
 *   whether the other language is filled in, and what does not look missing does
 *   not get written — which is how a bilingual platform ends up with an English
 *   catalogue.
 * * **Each input carries its own `dir` and `lang`.** An Arabic input inside an
 *   English interface must still lay out right-to-left and use the Arabic font
 *   stack (NFR-I2), and it is the only place in the app where direction is not
 *   simply inherited from `<html>`.
 * * **The English input is marked required** where the API requires it: English
 *   is the fallback language, so a course with only an Arabic title reads blank
 *   to every English visitor.
 */
import type { UseFormRegisterReturn } from 'react-hook-form'

import {
  directionFor,
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  type Language,
} from '@/shared/i18n/config'

import { cn } from './cn'
import { Input, Textarea } from './controls'
import { Field } from './Field'

export type TranslatedFieldProps = {
  /** The label, already translated — e.g. "Title". The language name is added. */
  label: string
  /** Per-language `register()` result. */
  inputs: Record<Language, UseFormRegisterReturn>
  /** Per-language error messages. */
  errors?: Partial<Record<Language, string | undefined>>
  /** Languages whose input is mandatory. */
  requiredLanguages?: readonly Language[] | undefined
  hint?: string | undefined
  multiline?: boolean | undefined
  rows?: number | undefined
  className?: string | undefined
}

export function TranslatedField({
  label,
  inputs,
  errors,
  requiredLanguages = ['en'],
  hint,
  multiline = false,
  rows,
  className,
}: TranslatedFieldProps) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', className)}>
      {SUPPORTED_LANGUAGES.map((language) => {
        const direction = directionFor(language)
        // "Title (English)" / "Title (العربية)" — the language name is never
        // translated, so it reads the same to both audiences.
        const languageLabel = `${label} (${LANGUAGE_LABELS[language]})`

        return (
          <Field
            key={language}
            label={languageLabel}
            error={errors?.[language]}
            hint={language === 'en' ? hint : undefined}
            required={requiredLanguages.includes(language)}
          >
            {multiline ? (
              <Textarea dir={direction} lang={language} rows={rows} {...inputs[language]} />
            ) : (
              <Input dir={direction} lang={language} {...inputs[language]} />
            )}
          </Field>
        )
      })}
    </div>
  )
}
